import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useGridCopyMenu } from './gridCommon';
import { getMainAxios } from '../utils/util';
import StockChart from './StockChart';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface MasterRow {
  prodId: string;
  prodNm: string;
  market: string | null;
  grpCode: string | null;
  mktamt: number | null;
  clsprc: number | null;
  facevalue: number | null;
  ltdstknum: number | null;
}

interface DailyRow {
  date: string;
  o: number; h: number; l: number; c: number; v: number;
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '2px solid #3b82f6',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const cardTitleStyle: React.CSSProperties = {
  marginBottom: 8, fontSize: 12, color: '#1e40af', fontWeight: 600,
};

const num = (v: any) => (v == null ? '' : Number(v).toLocaleString());

/** 주식 — 종목마스터 + 차트 + 일별시세 한 화면 */
const StockData: React.FC = () => {
  const axios = getMainAxios();
  const copyMenu = useGridCopyMenu();

  const [rows, setRows] = useState<MasterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [market, setMarket] = useState<'' | 'KOSPI' | 'KOSDAQ'>('');

  const [selected, setSelected] = useState<MasterRow | null>(null);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [dailyLoading, setDailyLoading] = useState(false);

  const fetchMaster = useCallback(async () => {
    setLoading(true);
    try {
      // kisproduct 는 일자별 누적 테이블이므로 최신일 1일치만 요청 (전체는 34만행)
      const r = await axios.get('/api/v1/stocks/master/list', {
        params: { market: market || undefined, search: search || undefined, latestOnly: true },
      });
      setRows(r.data ?? []);
    } catch (e) {
      console.error('[StockData] 마스터 조회 실패', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [axios, market, search]);

  const fetchDaily = useCallback(async (prodId: string) => {
    setDailyLoading(true);
    try {
      const r = await axios.get('/api/v1/quant/screener/history', { params: { prodId } });
      setDaily(r.data ?? []);
    } catch (e) {
      console.error('[StockData] 일별시세 조회 실패', e);
      setDaily([]);
    } finally {
      setDailyLoading(false);
    }
  }, [axios]);

  useEffect(() => { fetchMaster(); }, [fetchMaster]);

  // 목록 로드 후 첫 종목 자동 선택
  useEffect(() => {
    if (rows.length > 0 && !selected) {
      setSelected(rows[0]);
      fetchDaily(rows[0].prodId);
    }
  }, [rows, selected, fetchDaily]);

  const onSelect = (r: MasterRow) => {
    setSelected(r);
    fetchDaily(r.prodId);
  };

  const masterCols = useMemo<ColDef[]>(() => [
    { headerName: '종목코드', field: 'prodId', width: 100,
      cellStyle: { fontFamily: 'monospace', fontWeight: 600 } },
    { headerName: '종목명', field: 'prodNm', flex: 1, minWidth: 130 },
    { headerName: '시장', field: 'market', width: 80, cellStyle: { textAlign: 'center' } },
    { headerName: '시가총액', field: 'mktamt', width: 120, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
  ], []);

  const dailyCols = useMemo<ColDef[]>(() => [
    { headerName: '일자', field: 'date', width: 100, cellStyle: { textAlign: 'center' } },
    { headerName: '시가', field: 'o', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '고가', field: 'h', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '저가', field: 'l', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '종가', field: 'c', flex: 1, minWidth: 85, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 } },
    { headerName: '거래량', field: 'v', flex: 1, minWidth: 100, valueFormatter: p => num(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
  ], []);

  return (
    <div style={{ padding: 16, backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 상단 검색 바 */}
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: 14, fontWeight: 600 }}>주식 데이터</h3>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') fetchMaster(); }}
            placeholder="종목코드 / 종목명"
            style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, width: 220 }}
          />
          <select
            value={market}
            onChange={e => setMarket(e.target.value as any)}
            style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4 }}
          >
            <option value="">전체시장</option>
            <option value="KOSPI">KOSPI</option>
            <option value="KOSDAQ">KOSDAQ</option>
          </select>
          <button
            onClick={fetchMaster}
            disabled={loading}
            style={{
              padding: '4px 12px', background: '#3b82f6', color: 'white', border: 'none',
              borderRadius: 4, cursor: loading ? 'wait' : 'pointer', fontSize: 12, fontWeight: 500,
            }}
          >
            {loading ? '⏳ 조회 중...' : '🔍 조회'}
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
            {rows.length.toLocaleString()}종목
          </span>
        </div>
      </div>

      {/* 본문: 좌(종목마스터) / 우(차트 + 일별시세) */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ ...cardStyle, flex: '0 0 420px', marginBottom: 0 }}>
          <div style={cardTitleStyle}>종목마스터 ({rows.length.toLocaleString()}건)</div>
          <div className="ag-theme-quartz" style={{ height: 760, width: '100%', fontSize: 12 }}>
            <AgGridReact<MasterRow>
              rowData={rows}
              columnDefs={masterCols}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              onRowClicked={e => { if (e.data) onSelect(e.data); }}
              getRowStyle={p => (p.data?.prodId === selected?.prodId ? { backgroundColor: '#dbeafe' } : undefined)}
              rowHeight={30}
              headerHeight={32}
              {...copyMenu.gridProps}
            />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* 차트 — 기간(분/일/주/월) 토글은 StockChart 내장 */}
          <div style={{ ...cardStyle, marginBottom: 12 }}>
            <div style={cardTitleStyle}>
              차트 {selected ? `— ${selected.prodId} ${selected.prodNm}` : ''}
            </div>
            {!selected ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
                좌측에서 종목을 선택하세요.
              </div>
            ) : (
              <StockChart prodId={selected.prodId} prodNm={selected.prodNm} height={360} />
            )}
          </div>

          {/* 일별시세 */}
          <div style={{ ...cardStyle, marginBottom: 0 }}>
            <div style={cardTitleStyle}>
              일별시세 {selected ? `— ${selected.prodId}` : ''}
              {dailyLoading ? ' (조회 중...)' : ` (${daily.length}건)`}
            </div>
            <div className="ag-theme-quartz" style={{ height: 340, width: '100%', fontSize: 12 }}>
              <AgGridReact<DailyRow>
                rowData={[...daily].reverse()}
                columnDefs={dailyCols}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                rowHeight={28}
                headerHeight={32}
                {...copyMenu.gridProps}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockData;
